import { compareMasterSnapshots, masterFieldLabel, type MasterItemDiff } from "@/lib/master-diff";

interface MasterDataComparisonProps {
  left: unknown;
  right: unknown;
}

function valueText(value: unknown): string {
  if (value === undefined || value === null || value === "") return "없음";
  if (typeof value === "boolean") return value ? "예" : "아니오";
  if (typeof value === "number") return new Intl.NumberFormat("ko-KR", { maximumFractionDigits: 6 }).format(value);
  if (typeof value === "string") return value;
  const compact = JSON.stringify(value);
  return compact && compact.length > 160 ? `${compact.slice(0, 157)}...` : compact ?? "없음";
}

function changeLabel(change: MasterItemDiff["change"]): string {
  if (change === "added") return "추가됨";
  if (change === "removed") return "삭제됨";
  return "변경됨";
}

function changeClass(change: MasterItemDiff["change"]): string {
  if (change === "added") return "badge-accent";
  if (change === "removed") return "badge-danger";
  return "badge-review";
}

function originText(value: "db" | "default" | null): string {
  if (value === "db") return "DB 기준 데이터";
  if (value === "default") return "내장 기본값";
  return "기록 없음";
}

function changedValue(item: MasterItemDiff, field: string, side: "before" | "after") {
  const record = item[side];
  return valueText(record?.[field]);
}

export function MasterDataComparison({ left, right }: MasterDataComparisonProps) {
  const diff = compareMasterSnapshots(left, right);
  const changedCollections = diff.collections.filter((collection) => collection.items.length > 0);
  const unchangedCollections = diff.collections.filter((collection) => collection.items.length === 0);

  return (
    <div className="workspace-master-comparison">
      <div className="workspace-card-head">
        <div>
          <h3 className="workspace-card-title">마스터 데이터 변경</h3>
          <p className="workspace-card-desc">두 실행 기록에 저장된 배출계수와 기준 데이터를 항목별로 비교합니다.</p>
        </div>
      </div>
      <div className="workspace-master-summary">
        <div><span>변경된 배출계수</span><strong>{diff.changedFactorCount}건</strong></div>
        <div><span>변경된 기준 데이터</span><strong>{diff.changedReferenceCount}건</strong></div>
        <div><span>데이터 출처 변경</span><strong>{diff.originChanges.length}종류</strong></div>
      </div>

      {diff.changedItemCount === 0 && diff.originChanges.length === 0 ? (
        <p className="workspace-empty">두 실행 기록 사이에 마스터 데이터 변경이 없습니다.</p>
      ) : (
        <>
          {diff.originChanges.length > 0 && (
            <div className="workspace-master-origin" role="status">
              <strong>데이터 출처가 바뀐 종류</strong>
              <div className="workspace-master-origin-list">
                {diff.originChanges.map((change) => <span key={change.key}>{change.label}: {originText(change.before)}에서 {originText(change.after)}으로 변경</span>)}
              </div>
            </div>
          )}
          {changedCollections.map((collection) => (
            <section key={collection.key} className="workspace-master-section" aria-labelledby={`master-${collection.key}`}>
              <div className="workspace-master-section-head">
                <h4 id={`master-${collection.key}`}>{collection.label}</h4>
                <span className="badge-muted">{collection.items.length}건 변경</span>
              </div>
              <div className="table-scroll">
                <table className="admin-table workspace-master-table">
                  <thead><tr><th>항목</th><th>변경 상태</th><th>변경 필드</th><th>기준 기록</th><th>비교 기록</th></tr></thead>
                  <tbody>
                    {collection.items.map((item) => (
                      <tr key={`${item.collection}:${item.key}`}>
                        <td><strong>{item.label}</strong><span className="workspace-master-key">{item.key}</span></td>
                        <td><span className={changeClass(item.change)}>{changeLabel(item.change)}</span></td>
                        <td><div className="workspace-master-fields">{item.changedFields.map((field) => <span key={field} className="badge-muted">{masterFieldLabel(field)}</span>)}</div></td>
                        <td><div className="workspace-master-values">{item.changedFields.map((field) => <span key={field}><b>{masterFieldLabel(field)}</b>{changedValue(item, field, "before")}</span>)}</div></td>
                        <td><div className="workspace-master-values">{item.changedFields.map((field) => <span key={field}><b>{masterFieldLabel(field)}</b>{changedValue(item, field, "after")}</span>)}</div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
          {changedCollections.length === 0 && <p className="workspace-empty">항목 값은 같고 데이터 출처만 변경되었습니다.</p>}
        </>
      )}
      {changedCollections.length > 0 && unchangedCollections.length > 0 && (
        <p className="workspace-master-unchanged">변경이 없는 데이터 종류: {unchangedCollections.map((collection) => collection.label).join(", ")}</p>
      )}
    </div>
  );
}
